import { useState } from "react";
import { Page, useCurrent } from "./common";
import { PageHead, Modal, Meter } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions, STUDIO } from "../lib/store";
import { useCan, useSession } from "../lib/auth";
import { storeImage } from "../lib/sync";
import { fileToDataUri } from "../lib/image";
import { fmtDate, relative } from "../lib/format";
import { PhotoPins } from "./PhotoPins";

export default function Site() {
  const p = useCurrent();
  const canEdit = useCan("stages");
  const session = useSession();
  const who = session?.name ?? STUDIO.principal;
  const [add, setAdd] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [zoom, setZoom] = useState<string | null>(null);
  if (!p) return null;
  const log = [...(p.siteLog ?? [])].sort((a, b) => +new Date(b.on) - +new Date(a.on));
  const latestPct = log.find((u) => typeof u.percent === "number")?.percent;

  return (
    <Page>
      <PageHead kicker={`${p.name} · ${p.code}`} title="Site progress" />
      <p className="muted reveal" style={{ maxWidth: 640, marginBottom: 18 }}>
        Construction-monitoring updates from site — photos, notes and percent-complete — kept as a dated log the client can follow on their portal.
      </p>
      <div className="row wrap reveal" style={{ gap: 8, marginBottom: 20, alignItems: "center" }}>
        <span className="badge"><Icon.image width={11} />{log.length} update{log.length === 1 ? "" : "s"}</span>
        {typeof latestPct === "number" && <span className="badge accent">Latest {latestPct}% complete</span>}
        {(p.photoPins ?? []).length > 0 && <span className="badge">{(p.photoPins ?? []).length} pinned photo{(p.photoPins ?? []).length === 1 ? "" : "s"}</span>}
        <span style={{ flex: 1 }} />
        {canEdit && <button className="btn ghost sm" title="Pin a site photo to a point on a drawing" onClick={() => setPinOpen(true)}><Icon.pin width={14} />Pin to a drawing</button>}
        {canEdit && <button className="btn accent sm" onClick={() => setAdd(true)}><Icon.spark width={14} />Post an update</button>}
      </div>

      <PhotoPins p={p} canEdit={canEdit} by={who} open={pinOpen} onClose={() => setPinOpen(false)} />

      {log.length === 0 ? (
        <div className="card pad reveal" style={{ maxWidth: 560 }}>
          <div className="kicker">No site updates yet</div>
          <p className="muted" style={{ fontSize: 14, marginTop: 8, lineHeight: 1.6 }}>Post progress from site with photos and a percent-complete — the client sees each update on their Progress tab.</p>
          {canEdit && <button className="btn accent" style={{ marginTop: 14 }} onClick={() => setAdd(true)}><Icon.spark width={15} />Post the first update</button>}
        </div>
      ) : (
        <div className="col" style={{ gap: 14 }}>
          {log.map((u) => (
            <div key={u.id} className="card pad reveal">
              <div className="row between wrap" style={{ gap: 10 }}>
                <span style={{ fontSize: 15, fontWeight: 600 }}>{u.title}</span>
                <span className="dim" style={{ fontSize: 12 }}>{[u.zone, `${u.by}`, relative(u.on)].filter(Boolean).join(" · ")}</span>
              </div>
              {typeof u.percent === "number" && (
                <div className="row" style={{ gap: 10, marginTop: 8, alignItems: "center" }}>
                  <div style={{ flex: 1 }}><Meter value={u.percent} /></div>
                  <span className="mono" style={{ fontSize: 12.5, fontWeight: 600 }}>{u.percent}%</span>
                </div>
              )}
              {u.note && <div style={{ fontSize: 13.5, marginTop: 8, lineHeight: 1.55 }}>{u.note}</div>}
              {u.photos.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(140px,1fr))", gap: 8, marginTop: 10 }}>
                  {u.photos.map((ph, i) => <button key={i} onClick={() => setZoom(ph)} style={{ padding: 0, border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden", cursor: "zoom-in", background: "none", lineHeight: 0 }}><img src={ph} alt="" style={{ width: "100%", height: 110, objectFit: "cover", display: "block" }} /></button>)}
                </div>
              )}
              {canEdit && <div className="row" style={{ justifyContent: "flex-end", marginTop: 10 }}><button className="btn ghost sm" onClick={() => { if (confirm("Remove this update?")) actions.removeSiteUpdate(p.id, u.id); }}><Icon.close width={13} />Remove</button></div>}
            </div>
          ))}
        </div>
      )}

      {add && <AddSiteModal p={p} by={who} onClose={() => setAdd(false)} />}
      {zoom && <div onClick={() => setZoom(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.8)", display: "grid", placeItems: "center", zIndex: 100, padding: 24, cursor: "zoom-out" }}><img src={zoom} alt="" style={{ maxWidth: "92vw", maxHeight: "88vh", borderRadius: 10 }} /></div>}
    </Page>
  );
}

function AddSiteModal({ p, by, onClose }: { p: any; by: string; onClose: () => void }) {
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [zone, setZone] = useState("");
  const [percent, setPercent] = useState("");
  const [photos, setPhotos] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  async function addPhotos(files: FileList | null) {
    if (!files?.length) return;
    setBusy(true);
    for (const f of Array.from(files)) {
      try { const src = await storeImage(await fileToDataUri(f), p.client?.code); setPhotos((x) => [...x, src]); } catch { /* off */ }
    }
    setBusy(false);
  }
  return (
    <Modal open onClose={busy ? () => {} : onClose} title="Post a site update">
      <div className="col" style={{ gap: 14 }}>
        <div className="field"><label>Title</label><input className="input" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Ground-floor slab poured" /></div>
        <div className="row" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1 }}><label>Zone / element <span className="dim">(optional)</span></label><input className="input" value={zone} onChange={(e) => setZone(e.target.value)} placeholder="Main Building · Level 1" /></div>
          <div className="field" style={{ width: 130 }}><label>% complete</label><input className="input mono" type="number" min={0} max={100} value={percent} onChange={(e) => setPercent(e.target.value)} placeholder="45" /></div>
        </div>
        <div className="field"><label>Note <span className="dim">(optional)</span></label><textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What was done, what's next, any issues." /></div>
        <label className="panel row between" style={{ padding: "11px 14px", borderStyle: "dashed", cursor: "pointer" }}>
          <span className="row" style={{ gap: 9 }}><Icon.image width={16} style={{ color: "var(--accent-ink)" }} /><span style={{ fontSize: 13.5 }}>{busy ? "Uploading…" : photos.length ? `${photos.length} photo${photos.length === 1 ? "" : "s"} added` : "Add site photos"}</span></span>
          <span className="btn soft sm">Browse</span>
          <input type="file" accept="image/*" multiple hidden disabled={busy} onChange={(e) => addPhotos(e.target.files)} />
        </label>
        {photos.length > 0 && <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(80px,1fr))", gap: 6 }}>{photos.map((ph, i) => <img key={i} src={ph} alt="" style={{ width: "100%", height: 60, objectFit: "cover", borderRadius: 6, border: "1px solid var(--line)" }} />)}</div>}
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={busy || !title.trim()} onClick={() => { actions.addSiteUpdate(p.id, { title, note, zone, percent: percent ? Number(percent) : undefined, photos, by }); onClose(); }}><Icon.check width={15} />Post update</button>
        </div>
      </div>
    </Modal>
  );
}
